import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  SECTION_TYPES,
  createSectionByType,
  duplicateSection,
  getSectionIcon,
  getSectionLabel,
} from './sectionRegistry'
import useTranslation from '../../hooks/useTranslation'

const stripHtml = (html) => {
  if (!html || typeof html !== 'string') return ''
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

const truncate = (text, max = 40) => (
  text.length > max ? text.slice(0, max).trimEnd() + '…' : text
)

function getSectionSubtitle(section, t) {
  const settings = section.settings || {}
  const pluralize = (count, base) => t(
    `labels.themes.${base}${count === 1 ? 'Singular' : 'Plural'}`,
    { count }
  )
  switch (section.type) {
    case 'collection':
      return settings.sourceName || settings.cssKey || ''
    case 'categoryTiles': {
      if (settings.title) return settings.title
      if (Array.isArray(settings.items) && settings.items.length) {
        return pluralize(settings.items.length, 'tileCount')
      }
      return settings.cssKey || ''
    }
    case 'heroBanner': {
      const count = Array.isArray(settings.slides) ? settings.slides.length : 0
      if (count) return pluralize(count, 'slideCount')
      return settings.cssKey || ''
    }
    case 'contentBlock': {
      const text = truncate(stripHtml(settings.text || settings.header))
      return text || settings.cssKey || ''
    }
    default:
      return settings.cssKey || ''
  }
}

function SortableItem({
  section,
  selected,
  onSelect,
  onToggleVisible,
  onDelete,
  onDuplicate,
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: section.id,
  })

  const Icon = getSectionIcon(section.type)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <ListItemButton
      ref={setNodeRef}
      style={style}
      selected={selected}
      onClick={() => onSelect(section.id)}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        alignItems: 'center',
      }}
    >
      <ListItemIcon
        sx={{ minWidth: 32, cursor: 'grab' }}
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <DragIndicatorIcon fontSize="small" color="action" />
      </ListItemIcon>

      <ListItemIcon sx={{ minWidth: 32 }}>
        <Icon fontSize="small" />
      </ListItemIcon>

      <ListItemText
        primary={getSectionLabel(section.type)}
        secondary={getSectionSubtitle(section, t)}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
      />

      <Stack direction="row" spacing={0} alignItems="center">
        <Switch
          size="small"
          checked={section.visible !== false}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleVisible(section.id)}
        />

        <Tooltip title={t('labels.themes.duplicate')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onDuplicate(section.id)
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('labels.themes.delete')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(section.id)
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </ListItemButton>
  )
}

export default function SectionList({ sections, selectedId, onSelect, onChange }) {
  const { t } = useTranslation()
  const [addMenuAnchor, setAddMenuAnchor] = useState(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const sectionToDelete = sections.find((section) => section.id === deleteConfirmId)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sections.findIndex((section) => section.id === active.id)
    const newIndex = sections.findIndex((section) => section.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    onChange(arrayMove(sections, oldIndex, newIndex))
  }

  const handleToggleVisible = (sectionId) => {
    onChange(
      sections.map((section) =>
        section.id === sectionId
          // Mirror the UI's `section.visible !== false` semantics: undefined is
          // treated as visible, so toggling must produce explicit `false` to flip
          // off (otherwise undefined → !undefined → true → still shown).
          ? { ...section, visible: section.visible === false }
          : section
      )
    )
  }

  const handleDelete = (sectionId) => {
    setDeleteConfirmId(sectionId)
  }

  const confirmDelete = () => {
    if (!deleteConfirmId) return
    const nextSections = sections.filter((section) => section.id !== deleteConfirmId)
    onChange(nextSections)

    if (selectedId === deleteConfirmId) {
      onSelect(nextSections[0]?.id || null)
    }
    setDeleteConfirmId(null)
  }

  const handleDuplicate = (sectionId) => {
    const section = sections.find((item) => item.id === sectionId)
    if (!section) return

    const duplicated = duplicateSection(section, sections)
    const nextSections = [...sections]
    const index = nextSections.findIndex((item) => item.id === sectionId)
    nextSections.splice(index + 1, 0, duplicated)

    onChange(nextSections)
    onSelect(duplicated.id)
  }

  const handleAddSection = (type) => {
    const newSection = createSectionByType(type, sections)
    if (!newSection) return

    const nextSections = [...sections, newSection]
    onChange(nextSections)
    onSelect(newSection.id)
    setAddMenuAnchor(null)
  }

  return (
    <Box>
      <Box sx={{ p: 2, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2">{t('labels.themes.sectionsTitle')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('labels.themes.sectionsDescription')}
            </Typography>
          </Box>

          <IconButton size="small" onClick={(e) => setAddMenuAnchor(e.currentTarget)}>
            <AddIcon />
          </IconButton>
        </Stack>
      </Box>

      {sections.length === 0 ? (
        <Box
          sx={{
            mx: 1,
            mb: 1,
            p: 3,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t('labels.themes.noSections')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('labels.themes.noSectionsHint')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
          >
            {t('labels.themes.addSection')}
          </Button>
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <List dense disablePadding sx={{ px: 1 }}>
              {sections.map((section) => (
                <SortableItem
                  key={section.id}
                  section={section}
                  selected={selectedId === section.id}
                  onSelect={onSelect}
                  onToggleVisible={handleToggleVisible}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>{t('labels.themes.deleteSection')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('labels.themes.deleteSectionDescription', {
              name: sectionToDelete ? getSectionLabel(sectionToDelete.type) : '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>{t('labels.themes.cancel')}</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            {t('labels.themes.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
      >
        {SECTION_TYPES.map(({ type, label, description, Icon }) => (
          <MenuItem
            key={type}
            onClick={() => handleAddSection(type)}
            sx={{ alignItems: 'flex-start', py: 1, maxWidth: 360 }}
          >
            <ListItemIcon sx={{ mt: 0.5 }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={label}
              secondary={description}
              secondaryTypographyProps={{
                variant: 'caption',
                sx: { whiteSpace: 'normal' },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}